import { useEffect, useState } from 'react';
import { FileText, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Renders a chat attachment bubble (image thumbnail or file chip).
 * Both the inline image thumbnail and the "open full file" action go
 * through the caller's authenticated `api` client (blob fetch + object
 * URL) rather than a raw <img src="..."> / <a href target="_blank">,
 * since `api` can be any client shape (raw axios instance or a scoped
 * wrapper) and a plain URL/link depends on the browser reliably
 * attaching the session cookie to a sub-resource or brand-new tab —
 * not guaranteed in every embedding context. See StaffRxReviewPanel's
 * "View document" fix for the same root cause.
 */
export const ChatAttachmentView = ({ attachment, api, pathPrefix = '/chat/attachment', testId }) => {
  const isImage = attachment.content_type?.startsWith('image/');
  const [thumbUrl, setThumbUrl] = useState(null);

  useEffect(() => {
    if (!isImage) return;
    let revoked = false;
    let objectUrl = null;
    api.get(`${pathPrefix}/${attachment.asset_id}`, { responseType: 'blob' })
      .then((r) => { if (!revoked) { objectUrl = URL.createObjectURL(r.data); setThumbUrl(objectUrl); } })
      .catch(() => {});
    return () => { revoked = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachment.asset_id]);

  const open = async () => {
    const popup = window.open('', '_blank');
    try {
      const r = await api.get(`${pathPrefix}/${attachment.asset_id}`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      if (popup) popup.location = url; else window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      if (popup) popup.close();
      toast.error('Could not open attachment');
    }
  };

  if (isImage) {
    return (
      <button type="button" onClick={open} className="block mt-1" data-testid={testId}>
        {thumbUrl ? (
          <img src={thumbUrl} alt={attachment.filename}
            className="max-w-[200px] rounded-md border" style={{ maxHeight: '150px', objectFit: 'cover' }} />
        ) : (
          <span className="flex items-center justify-center w-[100px] h-[80px] rounded-md border bg-black/5">
            <Loader2 className="w-4 h-4 animate-spin opacity-60" />
          </span>
        )}
      </button>
    );
  }
  return (
    <button type="button" onClick={open} data-testid={testId}
      className="flex items-center gap-2 mt-1 p-2 rounded-md bg-white/20 hover:bg-white/30 transition-colors text-xs w-full text-left">
      <FileText className="w-4 h-4 shrink-0" />
      <span className="truncate max-w-[150px]">{attachment.filename}</span>
    </button>
  );
};
